import React from "react";
import { BeakerIcon } from "@heroicons/react/20/solid";
import { usePrimaryOrganizationId } from "../../../hooks";
import { usePrivacyCenter } from "../../../hooks/usePrivacyCenter";
import { useConsentRecordStats } from "../../../hooks/useConsentRecords";

const ACCEPTED_ICON = (
  <svg width="52" height="52" viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="52" height="52" rx="8" style={{ fill: "var(--color-green-100)" }} />
    <path
      fillRule="evenodd"
      d="M36.75 20.293a1 1 0 00-1.414-1.414L23 31.214l-5.336-5.335a1 1 0 00-1.414 1.414l6 6a1 1 0 001.414 0l14-14z"
      clipRule="evenodd"
      style={{ fill: "var(--color-green-500)" }}
    />
  </svg>
);

const DECLINED_ICON = (
  <svg width="52" height="52" viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="52" height="52" rx="8" style={{ fill: "var(--color-red-100)" }} />
    <path
      d="M36.75 17.325L34.675 15.25L26 23.925L17.325 15.25L15.25 17.325L23.925 26L15.25 34.675L17.325 36.75L26 28.075L34.675 36.75L36.75 34.675L28.075 26L36.75 17.325Z"
      style={{ fill: "var(--color-red-500)" }}
    />
  </svg>
);

const MIXED_ICON = (
  <div className="relative" style={{ width: "52px", height: "52px" }}>
    <svg width="52" height="52" viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="52" height="52" rx="8" style={{ fill: "var(--color-yellow-100)" }} />
    </svg>
    <div className="absolute inset-0 flex items-center justify-center">
      <BeakerIcon className="w-6 h-6" style={{ color: "var(--color-yellow-500)" }} />
    </div>
  </div>
);

const StatsGridItem = ({
  icon,
  title,
  count,
}: {
  icon: React.ReactNode;
  title: string;
  count: number;
}) => {
  return (
    <div
      className="border-[1.5px] border-solid border-neutral-400 rounded-[8px] p-4 flex"
      style={{ width: "216px", flexDirection: "column", gap: "24px" }}
    >
      <div className="flex items-center h-[57px]">
        <div>{icon}</div>
        <div className="ml-2">
          <div className="text-[14px] text-gray-500 font-medium">{title}</div>
          <div className="text-[24px] text-gray-900 font-bold">{count.toLocaleString()}</div>
        </div>
      </div>
    </div>
  );
};

export const ConsentRecordStats = ({ actionButton }: { actionButton?: React.ReactNode }) => {
  const orgId = usePrimaryOrganizationId();
  const [privacyCenter] = usePrivacyCenter(orgId);
  const [stats] = useConsentRecordStats(orgId, privacyCenter?.id);

  return (
    <div className="tw-boundary">
      <div className="flex justify-between items-center mt-6 mb-4">
        <h2 className="text-lg font-semibold">Consent Activity (Last 30 Days)</h2>
        {actionButton}
      </div>

      <div className="flex items-stretch" style={{ gap: "16px" }}>
        <StatsGridItem icon={ACCEPTED_ICON} title="Accepted" count={stats.accepted} />
        <StatsGridItem icon={DECLINED_ICON} title="Declined" count={stats.declined} />
        <StatsGridItem icon={MIXED_ICON} title="Mixed" count={stats.mixed} />
      </div>
    </div>
  );
};
